import React, { useState } from 'react';

const ProductImport = () => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      setResult(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError('Please select a file to import');
      return;
    }

    setImporting(true);
    setError(null);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('/api/calculator/import/products', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      const data = await response.json();

      if (data.success) {
        setResult(data);
        setFile(null);
        const fileInput = document.getElementById('file-input');
        if (fileInput) fileInput.value = '';
      } else {
        setError(data.error || 'Import failed');
      }
    } catch (err) {
      setError('Error uploading file: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await fetch('/api/calculator/import/products/template');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'product_import_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Error downloading template: ' + err.message);
    }
  };

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '24px' }}>
      <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', padding: '32px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '24px', color: '#111' }}>Import Products</h2>

        <div style={{ backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
          <h3 style={{ fontWeight: '600', color: '#1E3A8A', marginBottom: '8px' }}>Import Instructions:</h3>
          <ul style={{ marginLeft: '20px', fontSize: '14px', color: '#1E40AF', lineHeight: '1.6' }}>
            <li>Upload an Excel file (.xlsx or .xls)</li>
            <li>Column A: Product Name (required)</li>
            <li>Column B: Quantity/Size (required) - e.g., 1000, 250, 5</li>
            <li>Column C: Unit (optional, defaults to "Uses") - e.g., ml, g, items</li>
            <li>Column D: Price in £ (required)</li>
          </ul>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <button
            onClick={downloadTemplate}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              backgroundColor: '#F3F4F6',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            📥 Download Template
          </button>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
            Select Excel File
          </label>
          <input
            id="file-input"
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            style={{
              display: 'block',
              width: '100%',
              padding: '10px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
          {file && (
            <p style={{ marginTop: '8px', fontSize: '14px', color: '#6B7280' }}>
              Selected: {file.name}
            </p>
          )}
        </div>

        <button
          onClick={handleImport}
          disabled={!file || importing}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            backgroundColor: !file || importing ? '#D1D5DB' : '#2563EB',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: !file || importing ? 'not-allowed' : 'pointer'
          }}
        >
          📦 {importing ? 'Importing...' : 'Import Products'}
        </button>

        {error && (
          <div style={{
            marginTop: '24px',
            backgroundColor: '#FEF2F2',
            border: '1px solid #FCA5A5',
            borderRadius: '8px',
            padding: '16px'
          }}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <span style={{ fontSize: '20px' }}>⚠️</span>
              <div>
                <h4 style={{ fontWeight: '600', color: '#991B1B', marginBottom: '4px' }}>Error</h4>
                <p style={{ fontSize: '14px', color: '#B91C1C' }}>{error}</p>
              </div>
            </div>
          </div>
        )}

        {result && (
          <div style={{
            marginTop: '24px',
            backgroundColor: '#F0FDF4',
            border: '1px solid #BBF7D0',
            borderRadius: '8px',
            padding: '16px'
          }}>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <span style={{ fontSize: '20px' }}>✅</span>
              <div>
                <h4 style={{ fontWeight: '600', color: '#166534', marginBottom: '8px' }}>Import Completed</h4>
                <div style={{ fontSize: '14px', color: '#15803D' }}>
                  <p>✓ {result.imported} products imported successfully</p>
                  {result.skipped > 0 && <p>⚠ {result.skipped} products skipped</p>}
                </div>
              </div>
            </div>

            {result.errors && result.errors.length > 0 && (
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #BBF7D0' }}>
                <h5 style={{ fontWeight: '600', color: '#166534', marginBottom: '8px' }}>Details:</h5>
                <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #BBF7D0' }}>
                        <th style={{ textAlign: 'left', padding: '8px', fontWeight: '600' }}>Row</th>
                        <th style={{ textAlign: 'left', padding: '8px', fontWeight: '600' }}>Product</th>
                        <th style={{ textAlign: 'left', padding: '8px', fontWeight: '600' }}>Issue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.errors.map((err, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #D1FAE5' }}>
                          <td style={{ padding: '8px' }}>{err.row}</td>
                          <td style={{ padding: '8px' }}>{err.product}</td>
                          <td style={{ padding: '8px', color: '#D97706' }}>{err.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductImport;